import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingTracking } from "@/hooks/useOnboardingTracking";
import { supabase } from "@/integrations/supabase/client";

export type FlowType = "prebuilt" | "custom" | "braindump" | null;

export const useBoardCreationTracking = () => {
  const { user } = useAuth();
  const { trackEvent: trackOnboardingEvent } = useOnboardingTracking();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const currentStepRef = useRef<string | null>(null);
  const stepStartTimeRef = useRef<Date | null>(null);

  // Create a new tracking session
  const startTracking = async (flowType: FlowType = null) => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from("board_creation_sessions")
        .insert({
          user_id: user.id,
          flow_type: flowType,
          current_step: "path-selection",
        })
        .select()
        .single();

      if (error) throw error;
      setSessionId(data.id);
      
      // Track first step
      await trackStep(data.id, "path-selection");
    } catch (error) {
      console.error("Error starting tracking:", error);
    }
  };

  // Track step entry
  const trackStep = async (sessId: string, stepName: string) => {
    if (!sessId) return;

    try {
      // Close previous step if exists
      if (currentStepRef.current && stepStartTimeRef.current) {
        const duration = Math.floor((new Date().getTime() - stepStartTimeRef.current.getTime()) / 1000);
        
        await supabase
          .from("board_creation_step_timings")
          .update({
            exited_at: new Date().toISOString(),
            duration_seconds: duration,
          })
          .eq("session_id", sessId)
          .eq("step_name", currentStepRef.current)
          .is("exited_at", null);
      }

      // Start new step
      const { error } = await supabase
        .from("board_creation_step_timings")
        .insert({
          session_id: sessId,
          step_name: stepName,
        });

      if (error) throw error;

      currentStepRef.current = stepName;
      stepStartTimeRef.current = new Date();

      // Update current step in session
      await supabase
        .from("board_creation_sessions")
        .update({ current_step: stepName })
        .eq("id", sessId);
    } catch (error) {
      console.error("Error tracking step:", error);
    }
  };

  // Update flow type
  const updateFlowType = async (flowType: FlowType) => {
    if (!sessionId) return;

    try {
      await supabase
        .from("board_creation_sessions")
        .update({ flow_type: flowType })
        .eq("id", sessionId);
    } catch (error) {
      console.error("Error updating flow type:", error);
    }
  };

  // Mark as completed
  const markCompleted = async (boardId: string) => {
    if (!sessionId) return;

    try {
      // Close current step
      if (currentStepRef.current && stepStartTimeRef.current) {
        const duration = Math.floor((new Date().getTime() - stepStartTimeRef.current.getTime()) / 1000);
        
        await supabase
          .from("board_creation_step_timings")
          .update({
            exited_at: new Date().toISOString(),
            duration_seconds: duration,
          })
          .eq("session_id", sessionId)
          .eq("step_name", currentStepRef.current)
          .is("exited_at", null);
      }

      await supabase
        .from("board_creation_sessions")
        .update({
          completed_at: new Date().toISOString(),
          board_id: boardId,
        })
        .eq("id", sessionId);

      // Track board creation in onboarding analytics
      await trackOnboardingEvent("board_created", { boardId });
    } catch (error) {
      console.error("Error marking completed:", error);
    }
  };

  // Mark as abandoned (on unmount without completion)
  const markAbandoned = async () => {
    if (!sessionId) return;

    try {
      // Close current step
      if (currentStepRef.current && stepStartTimeRef.current) {
        const duration = Math.floor((new Date().getTime() - stepStartTimeRef.current.getTime()) / 1000);
        
        await supabase
          .from("board_creation_step_timings")
          .update({
            exited_at: new Date().toISOString(),
            duration_seconds: duration,
          })
          .eq("session_id", sessionId)
          .eq("step_name", currentStepRef.current)
          .is("exited_at", null);
      }

      // Check if already completed
      const { data } = await supabase
        .from("board_creation_sessions")
        .select("completed_at")
        .eq("id", sessionId)
        .single();

      if (!data?.completed_at) {
        await supabase
          .from("board_creation_sessions")
          .update({ abandoned_at: new Date().toISOString() })
          .eq("id", sessionId);
      }
    } catch (error) {
      console.error("Error marking abandoned:", error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionId) {
        markAbandoned();
      }
    };
  }, [sessionId]);

  return {
    sessionId,
    startTracking,
    trackStep,
    updateFlowType,
    markCompleted,
    markAbandoned,
  };
};
